import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronDown, ChevronRight, Truck, Plus, Trash2 } from "lucide-react";
import { SUPPLIER_SHIPPING_TYPES } from "@/components/quote/constants";

export const SupplierShippingCard = ({
  job,
  expandedSections,
  toggleSection,
  totalSupplierShipping,
  formatCurrency,
  addSupplierShipping,
  updateSupplierShipping,
  removeSupplierShipping,
}) => {
  return (
    <Collapsible
      open={expandedSections.supplierShipping}
      onOpenChange={() => toggleSection('supplierShipping')}
    >
    <div className="bg-amber-50/50 rounded-lg border border-amber-200/50 shadow-sm" data-testid="supplier-shipping-card">
      <CollapsibleTrigger asChild>
      <div className="px-5 py-4 cursor-pointer hover:bg-amber-100/30 transition-colors rounded-t-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-amber-800 flex items-center gap-2">
            {expandedSections.supplierShipping ? <ChevronDown className="w-4 h-4 text-amber-500" /> : <ChevronRight className="w-4 h-4 text-amber-500" />}
            <Truck className="w-4 h-4 text-amber-600" />
            Supplier Shipping
            {totalSupplierShipping > 0 && (
              <span className="font-mono text-xs text-amber-700 font-normal">
                {formatCurrency(totalSupplierShipping)}
              </span>
            )}
          </h3>
          <Button
            onClick={(e) => { e.stopPropagation(); addSupplierShipping(); }}
            size="sm"
            variant="outline"
            className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
            data-testid="add-supplier-shipping-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
      <div className="px-5 pb-5 border-t border-amber-200/50">
        {job.supplier_shipping.length === 0 ? (
          <div className="text-center py-6 text-amber-600/60">
            <Truck className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No supplier shipping costs</p>
            <p className="text-xs">These costs are absorbed into per-garment pricing</p>
          </div>
        ) : (
          <div className="space-y-2 mt-4">
            {job.supplier_shipping.map((shipping, index) => (
              <div
                key={shipping.id}
                className="p-2 bg-amber-100/30 rounded-md space-y-2"
                data-testid={`supplier-shipping-row-${index}`}
              >
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6 space-y-1">
                    <Label className="text-xs text-amber-700">Description</Label>
                    <Select
                      value={shipping.description}
                      onValueChange={(v) => updateSupplierShipping(shipping.id, "description", v)}
                    >
                      <SelectTrigger data-testid={`supplier-shipping-desc-${index}`} className="bg-white h-9 text-sm border-amber-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPLIER_SHIPPING_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5 space-y-1">
                    <Label className="text-xs text-amber-700">Cost</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={shipping.cost || ""}
                      onChange={(e) =>
                        updateSupplierShipping(
                          shipping.id,
                          "cost",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      data-testid={`supplier-shipping-cost-${index}`}
                      className="font-mono bg-white h-9 text-sm border-amber-200"
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSupplierShipping(shipping.id)}
                      data-testid={`remove-supplier-shipping-${index}`}
                      className="text-amber-600 hover:text-red-600 hover:bg-red-50 h-9 w-9"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Quantity Break Allocation - Only show in QTY BREAK mode */}
                {job.is_quantity_break && job.quantity_break_settings?.length > 0 && (
                  <div className="flex items-center gap-2 pt-1 pl-1">
                    <Label className="text-[10px] text-amber-600 uppercase whitespace-nowrap">
                      Apply to:
                    </Label>
                    <div className="flex flex-wrap gap-1">
                      {job.quantity_break_settings.map((col, colIndex) => {
                        const isAllocated = !shipping.allocated_qty_breaks ||
                          shipping.allocated_qty_breaks.length === 0 ||
                          shipping.allocated_qty_breaks.includes(col.quantity);
                        return (
                          <button
                            key={colIndex}
                            onClick={() => {
                              const currentAllocations = shipping.allocated_qty_breaks || [];
                              let newAllocations;

                              if (currentAllocations.length === 0) {
                                newAllocations = [col.quantity];
                              } else if (currentAllocations.includes(col.quantity)) {
                                newAllocations = currentAllocations.filter(q => q !== col.quantity);
                                if (newAllocations.length === 0) {
                                  newAllocations = [];
                                }
                              } else {
                                newAllocations = [...currentAllocations, col.quantity];
                                if (newAllocations.length === job.quantity_break_settings.length) {
                                  newAllocations = [];
                                }
                              }

                              updateSupplierShipping(shipping.id, "allocated_qty_breaks", newAllocations);
                            }}
                            className={`
                              px-2 py-0.5 text-[10px] rounded-full font-medium transition-all
                              ${isAllocated
                                ? "bg-purple-100 text-purple-700 border border-purple-300"
                                : "bg-gray-100 text-gray-400 border border-gray-200"
                              }
                            `}
                            data-testid={`shipping-alloc-${index}-${col.quantity}`}
                          >
                            {col.quantity}
                          </button>
                        );
                      })}
                      {(!shipping.allocated_qty_breaks || shipping.allocated_qty_breaks.length === 0) && (
                        <span className="text-[10px] text-amber-500 italic ml-1">All</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </CollapsibleContent>
    </div>
    </Collapsible>
  );
};
